import { useContext } from 'react';
import { motion } from 'motion/react';
import { FeatureArticleContext } from '../Feature.context';
import { Icon } from '../../../components/icon/Icon';

import styles from './Menu.module.css';
import { AppContext } from '../../../App.context';
import config from '../../../config';
import { TrackingContext as TrackingContext } from '../../../tracking/Tracking.context';
import { TrackingData } from '../../../tracking/Tracking.events';

const MIN_FONT_SIZE = 30,
	MAX_FONT_SIZE = 100,
	FONT_STEP = 10;

export class Button {
	static Share = ShareButton;
	static Font = FontSizeButton;
	static Legal = Legal;
}

function Legal() {
	const components = [];
	const { legal } = config.app;
	if (!legal) {
		return null;
	}
	if (legal.tou) {
		components.push(
			<a href={legal.tou.url} target="_blank" rel="noreferrer">
				{legal.tou.label}
			</a>,
		);
	}
	if (legal.privacy) {
		components.push(
			<a href={legal.privacy.url} target="_blank" rel="noreferrer">
				{legal.privacy.label}
			</a>,
		);
	}
	components.push(<div className="optanon-toggle-display">Do Not Sell</div>);
	if (legal.cookie) {
		components.push(
			<a href={legal.cookie.url} target="_blank" rel="noreferrer">
				{legal.cookie.label}
			</a>,
		);
	}
	return (
		<div className={styles['legal']}>
			<div>{components}</div>
		</div>
	);
}

function FontSizeButton() {
	const animations = {
		hover: {
			scale: 1.1,
		},
		tap: {
			scale: 0.8,
		},
	};
	return (
		<div className={styles['font-button']}>
			<DecreaseFontButton animations={animations} />
			<IncreaseFontButton animations={animations} />
		</div>
	);
}

function DecreaseFontButton({ animations }: FontButton) {
	const { fontSize, setFontSize } = useContext(FeatureArticleContext);

	const title = 'Decrease font size';

	const onClick = () => {
		const newFontSize = fontSize - FONT_STEP;
		setFontSize(newFontSize <= MIN_FONT_SIZE ? MIN_FONT_SIZE : newFontSize);
	};

	const isDisabled = fontSize === MIN_FONT_SIZE;

	if (isDisabled) {
		return (
			<motion.button
				title={title}
				type={'button'}
				style={{ cursor: 'not-allowed' }}
				disabled
			>
				<Icon name={'font-decrease'} color={'#c3c3c3'} />
			</motion.button>
		);
	}

	return (
		<motion.button
			title={title}
			whileHover={animations.hover}
			whileTap={animations.tap}
			type={'button'}
			style={{ cursor: 'pointer' }}
			onClick={onClick}
		>
			<Icon name={'font-decrease'} color={'#000'} />
		</motion.button>
	);
}

function IncreaseFontButton({ animations }: FontButton) {
	const { fontSize, setFontSize } = useContext(FeatureArticleContext);

	const title = 'Increase font size';

	const onClick = () => {
		const newFontSize = fontSize + FONT_STEP;
		setFontSize(newFontSize >= MAX_FONT_SIZE ? MAX_FONT_SIZE : newFontSize);
	};

	const isDisabled = fontSize === MAX_FONT_SIZE;

	if (isDisabled) {
		return (
			<motion.button
				title={title}
				type={'button'}
				style={{ cursor: 'not-allowed' }}
				disabled
			>
				<Icon name={'font-increase'} color={'#c3c3c3'} />
			</motion.button>
		);
	}

	return (
		<motion.button
			title={title}
			whileHover={animations.hover}
			whileTap={animations.tap}
			type={'button'}
			style={{ cursor: 'pointer' }}
			onClick={onClick}
		>
			<Icon name={'font-increase'} color={'#000'} />
		</motion.button>
	);
}

interface FontButton {
	animations: {
		hover: {
			scale: number;
		};
		tap: {
			scale: number;
		};
	};
}

function ShareButton() {
	const { lang } = useContext(AppContext);
	const { sendEvent } = useContext(TrackingContext);
	const { article } = useContext(FeatureArticleContext);
	if (!article) {
		return null;
	}
	const title: string = article.name[lang] || undefined;
	const url: string = window.location.href;
	const message = 'URL copy to the clipboard';
	const eventData: TrackingData = {
		method: 'os',
		contentType: 'article',
		article: {
			id: parseInt(`${article.id}`, 10),
			ArticleID: parseInt(`${article.ArticleID}`, 10),
		},
	};

	const onClick = () => {
		if (navigator.share) {
			console.log(`I click share`);
			sendEvent({
				type: 'SHARE',
				data: eventData,
			});
			navigator
				.share({
					title,
					url,
				})
				.then((res) => {
					console.log('The article was shared', res);
				})
				.catch(console.error);

			return;
		}

		if (navigator.clipboard) {
			navigator.clipboard
				.writeText(url)
				.then(
					() => {
						eventData.method = 'clipboard';
						sendEvent({
							type: 'SHARE',
							data: eventData,
						});
						alert(message);
					},
					() => {
						alert('Fail copy the url');
					},
				)
				.catch(console.error);
			return;
		}

		const inputElement = document.createElement('input');
		document.body.appendChild(inputElement);
		inputElement.value = url;
		inputElement.select();
		document.execCommand('copy');
		document.body.removeChild(inputElement);
		eventData.method = 'clipboard';
		sendEvent({
			type: 'SHARE',
			data: eventData,
		});

		alert(message);
	};
	return (
		<button type={'button'} title={'Share'} onClick={onClick}>
			<span>Share</span>
			<span>
				<Icon name={'share'} color={'#000'} />
			</span>
		</button>
	);
}
